import { CandleInterval } from '@katanaperps/katana-perps-sdk';

import { UnreachableCaseError } from 'lib/utils';

import { ResolutionString } from './external/charting_library';
import { loadChartSettings } from './persistance/settingsAdapter';

/**
 * TradingView resolution types
 * @see https://github.com/tradingview/charting_library/wiki/Resolution
 */
const supportedResolutions = [
  '1',
  '5',
  '15',
  '30',
  '60',
  '240',
  '1D',
  // Below are using "1D" (daily) API response
  '3D',
  '1W',
  '1M',
] as const;

export const supportedResolutionsString =
  supportedResolutions as unknown as ResolutionString[];

export type SupportedResolutionsType = (typeof supportedResolutions)[number];

const defaultInterval: SupportedResolutionsType = '15';

export const getDefaultOrLastUsedIntervalString = (): ResolutionString => {
  const lastUsedTimeBasedResolution =
    loadChartSettings()?.['chart.lastUsedTimeBasedResolution']?.toString();
  if (
    lastUsedTimeBasedResolution &&
    supportedResolutions.includes(lastUsedTimeBasedResolution as any)
  ) {
    return lastUsedTimeBasedResolution as ResolutionString;
  }
  return defaultInterval as ResolutionString;
};

const convertSdkIntervalToTradingview = (
  interval: CandleInterval
): SupportedResolutionsType => {
  switch (interval) {
    case CandleInterval.ONE_MINUTE:
      return '1';
    case CandleInterval.FIVE_MINUTES:
      return '5';
    case CandleInterval.FIFTEEN_MINUTES:
      return '15';
    case CandleInterval.THIRTY_MINUTES:
      return '30';
    case CandleInterval.ONE_HOUR:
      return '60';
    case CandleInterval.FOUR_HOURS:
      return '240';
    case CandleInterval.ONE_DAY:
      return '1D';
    default:
      throw new UnreachableCaseError(interval);
  }
};

export const convertTradingviewIntervalToSdk = (
  interval: SupportedResolutionsType
): CandleInterval => {
  switch (interval) {
    case '1':
      return CandleInterval.ONE_MINUTE;
    case '5':
      return CandleInterval.FIVE_MINUTES;
    case '15':
      return CandleInterval.FIFTEEN_MINUTES;
    case '30':
      return CandleInterval.THIRTY_MINUTES;
    case '60':
      return CandleInterval.ONE_HOUR;
    case '240':
      return CandleInterval.FOUR_HOURS;
    // Also use daily for month and weekly stats
    case '1D':
    case '3D':
    case '1W':
    case '1M':
      return CandleInterval.ONE_DAY;
    default:
      throw new UnreachableCaseError(interval);
  }
};

const sdkInternalValues = Array.from(new Set(Object.values(CandleInterval)));

if (
  // Make sure we are always in sync with SDK
  // https://docs-perps.katana.network/#api-request-amp-response-values
  Array.from(sdkInternalValues)
    .map(convertSdkIntervalToTradingview)
    .some((interval, index) => supportedResolutions[index] !== interval)
) {
  // When this occurs, we should include/remove resolutions in types above
  console.error('Error: SDK chart resolutions out of sync');
}
